import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { OutcomeBadge } from "@/components/OutcomeBadge";
import { getDiagnostics } from "@/lib/api";

const STATE_TONE = {
    active: "text-green-700",
    unconfigured: "text-slate-600",
    rejected: "text-amber-700",
    unreachable: "text-red-700",
    error: "text-red-700",
};

export default function Diagnostics() {
    const [diag, setDiag] = useState(null);

    useEffect(() => {
        getDiagnostics().then(setDiag).catch(() => setDiag(null));
    }, []);

    return (
        <Layout>
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Restricted · diagnostics
            </p>
            <h1 className="mt-3 text-3xl font-medium tracking-tight text-slate-900">
                Node & environment diagnostics
            </h1>
            <p className="mt-3 max-w-3xl text-sm text-slate-600">
                Redacted operational view. No bearer tokens, authorization headers,
                certificates, raw evidence or signed receipt envelopes are exposed here.
            </p>

            {!diag ? (
                <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading diagnostics…
                </div>
            ) : (
                <div className="mt-8 space-y-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200">
                    {/* KATLAS node */}
                    <div className="bg-white p-6" data-testid="diag-katlas">
                        <h2 className="text-base font-medium text-slate-900">KATLAS live node</h2>
                        <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                            <Row label="Mode" value={diag.katlas.mode} mono />
                            <Row label="Node URL" value={diag.katlas.node_url} mono />
                            <Row
                                label="Node state"
                                value={
                                    <span className={`font-mono ${STATE_TONE[diag.katlas.node_state] || ""}`}>
                                        {diag.katlas.node_state} - {diag.katlas.node_label}
                                    </span>
                                }
                            />
                            <Row label="Bearer token presence" value={diag.katlas.token_presence} mono />
                            <Row label="Simulation fallback" value={String(diag.katlas.simulation_fallback_enabled)} mono />
                            <Row label="Timeout (s)" value={diag.katlas.timeout_seconds} mono />
                            <Row label="Live paths" value={diag.katlas.paths.join("  ·  ")} mono />
                        </dl>
                    </div>

                    {/* Environment */}
                    <div className="bg-white p-6" data-testid="diag-environment">
                        <h2 className="text-base font-medium text-slate-900">Environment</h2>
                        <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                            <Row label="DB name" value={diag.environment.db_name} mono />
                            <Row label="CORS origins" value={diag.environment.cors_origins} mono />
                            <Row label="Bearer token value exposed" value={String(diag.environment.bearer_token_value_exposed)} mono />
                            <Row label="Authorization header exposed" value={String(diag.environment.authorization_header_exposed)} mono />
                        </dl>
                    </div>

                    {/* Scenarios */}
                    <div className="bg-white p-6" data-testid="diag-scenarios">
                        <h2 className="text-base font-medium text-slate-900">Seeded synthetic scenarios</h2>
                        <ul className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                            {diag.seeded_scenarios.map((s) => (
                                <li key={s.event_id} className="flex items-center justify-between py-3">
                                    <span className="font-mono text-sm text-slate-700">{s.event_id}</span>
                                    <span className="flex items-center gap-3">
                                        <span className="font-mono text-xs text-slate-400">{s.reason_code}</span>
                                        <OutcomeBadge outcome={s.outcome} />
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {diag.outcomes_in_model.map((o) => (
                                <OutcomeBadge key={o} outcome={o} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2.5">
            <dt className="text-xs font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono break-all" : ""}`}>{value}</dd>
        </div>
    );
}
